import * as React from "react";
import * as LucideIcons from "lucide-react";

export type LucideIconComponent = React.ComponentType<{ className?: string; "aria-hidden"?: boolean; size?: number }>;

const FallbackIcon = LucideIcons.Circle as LucideIconComponent;

/**
 * Resolve a Lucide icon by name. Tries exact PascalCase, then capitalized form (e.g. `truck` → `Truck`).
 * Never throws; returns null if unknown.
 */
function toLucidePascalKey(raw: string): string {
    const parts = raw.split(/[-_\s]+/).filter(Boolean);
    if (parts.length === 0) return "";
    return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join("");
}

/** Squashed / legacy editor values → official Lucide export name */
const LUCIDE_ICON_ALIASES: Record<string, string> = {
    messagecircle: "MessageCircle",
    message_circle: "MessageCircle",
    whatsapp: "MessageCircle",
    support: "Headset",
    truck: "Truck",
    payment: "CreditCard",
};

export function resolveLucideIcon(name?: string | null): LucideIconComponent | null {
    if (name == null) return null;
    const raw = String(name).trim();
    if (!raw) return null;
    const map = LucideIcons as unknown as Record<string, LucideIconComponent>;
    try {
        if (typeof map[raw] === "function") return map[raw]!;
        const alias = LUCIDE_ICON_ALIASES[raw.toLowerCase()];
        if (alias && typeof map[alias] === "function") return map[alias]!;
        const pascal = toLucidePascalKey(raw);
        if (pascal && typeof map[pascal] === "function") return map[pascal]!;
        const simple = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
        if (simple !== pascal && typeof map[simple] === "function") return map[simple]!;
    } catch {
        /* ignore */
    }
    return null;
}

/** Renders a Lucide glyph by name, or a neutral fallback if invalid. */
export function SafeLucideIcon({
    name,
    className,
    "aria-hidden": ariaHidden = true,
}: {
    name: string;
    className?: string;
    "aria-hidden"?: boolean;
}) {
    const Icon = resolveLucideIcon(name) ?? FallbackIcon;
    try {
        return <Icon className={className} aria-hidden={ariaHidden} />;
    } catch {
        return <FallbackIcon className={className} aria-hidden={ariaHidden} />;
    }
}
